import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { PageHeader } from '@/components/PageHeader';
import { StatusDot } from '@/components/StatusDot';
import { ChevronLeft } from 'lucide-react';
import { formatDate, relativeTime, shortId } from '@/lib/utils';

export default function TaskDetail() {
  const { taskId } = useParams();
  const nav = useNavigate();
  const { data, isLoading } = useQuery({
    queryKey: ['task', taskId],
    queryFn: () => api.get(`/tasks/${taskId}`).then(r => r.data),
    refetchInterval: 5000,
  });

  if (isLoading) return <div className="text-sm text-[hsl(var(--text-3))]" data-testid="loading-skeleton">Loading task…</div>;
  if (!data?.task) return <div className="text-sm text-[hsl(var(--text-3))]">Task not found.</div>;
  const t = data.task;

  return (
    <div className="space-y-6" data-testid="task-detail-page">
      <button onClick={() => nav('/tasks')} className="inline-flex items-center gap-1 text-xs text-[hsl(var(--text-3))] hover:text-[hsl(var(--text-1))] transition-colors" data-testid="back-to-tasks-button">
        <ChevronLeft className="h-3.5 w-3.5" /> All tasks
      </button>
      <PageHeader
        eyebrow={`Task · ${t.kind}`}
        title={`Run ${shortId(t.id, 10)}`}
        description={<div className="flex items-center gap-3"><StatusDot state={t.status} />{t.verdict && <span className="rounded border border-white/10 bg-[hsl(var(--surface-2))] px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-wider text-[hsl(var(--text-2))]">verdict: {t.verdict}</span>}</div>}
      />

      <div className="grid gap-4 md:grid-cols-2">
        <div className="rounded-lg border border-white/5 bg-[hsl(var(--surface-1))]">
          <div className="border-b border-white/5 px-5 py-3 text-sm font-medium">Request</div>
          <div className="p-5 space-y-3">
            <div>
              <div className="text-xs uppercase tracking-wider text-[hsl(var(--text-3))]">Instruction</div>
              <p className="mt-1 text-sm whitespace-pre-wrap">{t.instruction}</p>
            </div>
            {t.target_url && (
              <div>
                <div className="text-xs uppercase tracking-wider text-[hsl(var(--text-3))]">Target URL</div>
                <a href={t.target_url} target="_blank" rel="noreferrer" className="mt-1 inline-block break-all font-mono text-xs text-[hsl(var(--primary))] hover:underline">{t.target_url}</a>
              </div>
            )}
            <div>
              <div className="text-xs uppercase tracking-wider text-[hsl(var(--text-3))]">Workflow</div>
              <p className="mt-1 font-mono text-xs text-[hsl(var(--text-2))]">{t.workflow_id || '—'}</p>
            </div>
          </div>
        </div>

        <div className="rounded-lg border border-white/5 bg-[hsl(var(--surface-1))]">
          <div className="border-b border-white/5 px-5 py-3 text-sm font-medium">Timeline</div>
          <div className="p-5 space-y-2 text-sm">
            <Row k="Queued" v={formatDate(t.created_at)} />
            <Row k="Started" v={t.started_at ? formatDate(t.started_at) : '—'} />
            <Row k="Completed" v={t.completed_at ? formatDate(t.completed_at) : '—'} />
            <Row k="Cost" v={`$${(t.cost_usd || 0).toFixed(4)}`} />
            {t.error && <Row k="Error" v={<span className="text-[hsl(var(--danger))] text-xs">{t.error}</span>} />}
          </div>
        </div>
      </div>

      <div className="rounded-lg border border-white/5 bg-[hsl(var(--surface-1))]">
        <div className="border-b border-white/5 px-5 py-3 text-sm font-medium">Events</div>
        <div className="p-3">
          {!data.events?.length ? (
            <div className="p-4 text-sm text-[hsl(var(--text-3))]">No events emitted yet.</div>
          ) : (
            <div className="rounded-md border border-white/10 bg-black/30 font-mono text-xs">
              {data.events.map((e, i) => (
                <div key={e.id} className="grid grid-cols-[auto_auto_1fr] gap-3 border-b border-white/5 px-3 py-2 last:border-0" data-testid={`log-line-${i}`}>
                  <span className="tabular text-white/40">{formatDate(e.created_at)}</span>
                  <span className="text-[hsl(var(--info))]">{e.kind}</span>
                  <span className="text-[hsl(var(--text-2))] truncate">{JSON.stringify(e.payload)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {t.result && Object.keys(t.result).length > 0 && (
        <div className="rounded-lg border border-white/5 bg-[hsl(var(--surface-1))]">
          <div className="border-b border-white/5 px-5 py-3 text-sm font-medium">Result</div>
          <pre className="max-h-[400px] overflow-auto p-5 text-xs font-mono text-[hsl(var(--text-2))]">{JSON.stringify(t.result, null, 2)}</pre>
        </div>
      )}
    </div>
  );
}

function Row({ k, v }) {
  return (
    <div className="grid grid-cols-[110px_1fr] items-baseline gap-2">
      <dt className="text-xs uppercase tracking-wider text-[hsl(var(--text-3))]">{k}</dt>
      <dd className="text-[hsl(var(--text-2))]">{v}</dd>
    </div>
  );
}
